import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CampaignIcon from "@mui/icons-material/Campaign";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const PendingProfitsDialog = ({
  open,
  onClose,
  campaigns,
  partnerships,
  onEditCampaign,
  onEditPartnership,
  formatAmount,
  formatDate,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter active items
  const activeCampaigns = campaigns; // Already filtered in parent
  const activePartnerships = partnerships; // Already filtered in parent


  // Calculate profits: 25% campaigns, 10% partnerships
  const campaignsWithProfit = activeCampaigns.map((c) => ({
    ...c,
    revenue: parseFloat(c.campaign_sum || 0),
    profit: parseFloat(c.campaign_sum || 0) * 0.25,
    profitMargin: 25,
  }));

  const partnershipsWithProfit = activePartnerships.map((p) => ({
    ...p,
    revenue: parseFloat(p.computed_total || 0),
    profit: parseFloat(p.computed_total || 0) * 0.1,
    profitMargin: 10,
  }));

  const totalCampaignsProfit = campaignsWithProfit.reduce(
    (sum, c) => sum + c.profit,
    0
  );

  const totalPartnershipsProfit = partnershipsWithProfit.reduce(
    (sum, p) => sum + p.profit,
    0
  );

  const totalProfit = totalCampaignsProfit + totalPartnershipsProfit;

  const totalRevenue =
    campaignsWithProfit.reduce((s, c) => s + c.revenue, 0) +
    partnershipsWithProfit.reduce((s, p) => s + p.revenue, 0);

  const averageMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  // Search filtering
  const filteredCampaigns = campaignsWithProfit.filter(
    (c) =>
      searchTerm === "" ||
      c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPartnerships = partnershipsWithProfit.filter(
    (p) =>
      searchTerm === "" ||
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.creator?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Pending Profits Breakdown
              </Typography>
              <Chip
                label={`${averageMargin.toFixed(1)}% Avg Margin`}
                color="success"
                icon={<TrendingUpIcon />}
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Expected profits from active collaborations (25% campaigns, 10%
              partnerships)
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 2,
                mb: 3,
              }}
            >
              <Card sx={{ bgcolor: "#f0fdf4", border: "1px solid #86efac" }}>
                <CardContent sx={{ py: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Total Pending Profit
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#15803d", mt: 0.5 }}
                      >
                        {formatAmount(totalProfit)}
                      </Typography>
                    </Box>
                    <AccountBalanceIcon
                      sx={{ color: "#15803d", fontSize: 36 }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#ede9fe", border: "1px solid #c4b5fd" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Campaign Profits (25%)
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#7c3aed", mt: 0.5 }}
                  >
                    {formatAmount(totalCampaignsProfit)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    from {activeCampaigns.length} campaigns
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#dbeafe", border: "1px solid #93c5fd" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Partnership Profits (10%)
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#2563eb", mt: 0.5 }}
                  >
                    {formatAmount(totalPartnershipsProfit)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    from {activePartnerships.length} partnerships
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#fef3c7", border: "1px solid #fde68a" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#d97706", mt: 0.5 }}
                  >
                    {formatAmount(totalRevenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {averageMargin.toFixed(1)}% profit margin
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Tabs and Search */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => {
                  setActiveTab(val);
                  setSearchTerm("");
                }}
              >
                <Tab
                  label={`Campaigns (${activeCampaigns.length})`}
                  icon={<CampaignIcon />}
                  iconPosition="start"
                />
                <Tab
                  label={`Partnerships (${activePartnerships.length})`}
                  icon={<HandshakeIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 350 }}
              />
            </Box>

            {/* Campaigns Table */}
            {activeTab === 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Campaign Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Profit Margin
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Expected Profit
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Videos</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCampaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography color="text.secondary">
                            No active campaigns
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCampaigns.map((campaign) => (
                        <TableRow
                          key={campaign.id}
                          hover
                          sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CampaignIcon
                                sx={{ color: "#7c3aed", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {campaign.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatAmount(campaign.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${campaign.profitMargin}%`}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#15803d" }}
                            >
                              {formatAmount(campaign.profit)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {campaign.video_amount || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(campaign.proposal_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Campaign">
                              <IconButton
                                size="small"
                                onClick={() => onEditCampaign(campaign)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Partnerships Table */}
            {activeTab === 1 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Partnership
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Creator</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Profit Margin
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Expected Profit
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPartnerships.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography color="text.secondary">
                            No active partnerships
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPartnerships.map((partnership) => (
                        <TableRow
                          key={partnership.id}
                          hover
                          sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <HandshakeIcon
                                sx={{ color: "#2563eb", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {partnership.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partnership.creator}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatAmount(partnership.revenue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${partnership.profitMargin}%`}
                              size="small"
                              color="info"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#15803d" }}
                            >
                              {formatAmount(partnership.profit)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(partnership.proposal_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Partnership">
                              <IconButton
                                size="small"
                                onClick={() => onEditPartnership(partnership)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Profit Summary */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#f0fdf4",
                borderRadius: 2,
                border: "1px solid #86efac",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#15803d", mb: 1 }}
              >
                Profit Calculation Notes:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    • Campaigns: 25% profit margin
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    • Partnerships: 10% profit margin
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingProfitsDialog;